import { DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton, SearchBox } from "@fluentui/react";
import { useContext, useEffect, useMemo, useState } from "react";
import { Pod, PodAddProviderRequest } from "../../../api/pods.types";
import { MAX_POD_CLINICIANS } from "../../../config/constants";
import PodErrorDrawer from "../PodErrorDrawer";
import PodProviderSelector from "../PodProviderSelector";
import PodStatusSpinner, { PodStatusSpinnerState } from "../PodStatusSpinner";
import { PodStateContext } from "../state/reducer";
import { getMarketRegions, getProviderList } from "../state/selectors";
import { getProviderCategory, getProviderCategoryByPosition } from "../Util";
import styles from './AddProviderDialog.module.scss';

interface Props {
  show: boolean;
  loading?: boolean;
  error: string;
  pod?: Pod;
  onSubmit: (data: PodAddProviderRequest, skipAssignmentCheck?: boolean) => void;
  onDismiss: () => void;
}

const AddProviderDialog = ({show, pod, loading, error: parentError, onSubmit, onDismiss: dismissCallback}: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string>();
  const [errorDisable, setErrorDisable] = useState(false);

  const setError = (error?: string, shouldBlockSave?: boolean) => {
    setErrorText(error);
    if (!shouldBlockSave) {
      setErrorDisable(false);
    } else {
      setErrorDisable(true);
    }
  }

  const { useContextSelector } = useContext(PodStateContext);
  const providers = useContextSelector(getProviderList);
  const marketGroups = useContextSelector(getMarketRegions).marketGroups;
  const allProviders = useMemo(() => [...providers.physicians, ...providers.others], [providers]);

  const filteredProviders = useMemo(() => {
    // Enumerate the providerIds for all active members of the pod. Used to pare down search results.
    const providerIdsInPod = pod?.providers.filter(provider => provider.status === 'ACTIVATED').map(provider => provider.providerId) ?? [];
    const searchTermLowerCase = searchTerm.toLowerCase();

    const all = [...allProviders];
    all.sort((a,b) => a.fullName.localeCompare(b.fullName));

    return all.filter(provider => {
      // Remove all providers without a suffix, since we can't determine their position.
      if (!provider.suffix) return false;

      // Remove all providers who are already in the pod.
      const isAlreadyInPod = providerIdsInPod.indexOf(provider.id) !== -1;
      if (isAlreadyInPod) return false;

      const firstNameMatch = provider.firstName.toLowerCase().startsWith(searchTermLowerCase);
      const lastNameMatch = provider.lastName.toLowerCase().startsWith(searchTermLowerCase);
      const suffixMatch = provider.suffix.toLowerCase().startsWith(searchTermLowerCase);

      return firstNameMatch || lastNameMatch || suffixMatch;
    }).slice(0,10); // Limit results to two times the vertical capacity of the Selector's container.
  }, [searchTerm, allProviders, pod]);

  /**
   * If the selected item disappears due to the search term changing, reset the selection.
   */
  useEffect(() => {
    if (selectedProviderId && filteredProviders.findIndex(provider => provider.id === selectedProviderId) === -1) {
      setSelectedProviderId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProviders]);

  const submitCallback = () => {
    if (selectedProviderId === null) return;

    /**
     * PCCs are allowed to be assigned to multiple pods at once.
     * This check fails closed-- if the provider can't be found in the list, enforce the
     * assignment check, even if they are a PCC but we aren't aware due to a bug.
     */
    const providerSuffix = allProviders.find(provider => provider.id === selectedProviderId)?.suffix;
    const skipAssignmentCheck = providerSuffix === 'PCC';

    onSubmit({ providerId: selectedProviderId }, skipAssignmentCheck);
  };

  /**
   * Whenever the dialog is shown or hidden, reset it
   */
  useEffect(() => {
    let delay: NodeJS.Timeout | null = null;

    if (!show) {
      delay = setTimeout(() => {
        setSearchTerm('');
        setSelectedProviderId(null);
      }, 500);
    } else {
      setSearchTerm('');
      setSelectedProviderId(null);
    }

    return () => {
      if (delay) clearTimeout(delay);
    }
  }, [show]);

  /**
   * Expand the list of market groups in the pod into a full list of submarkets.
   */
  const allSubmarketsInPod: string[] = useMemo(() => {
    const allSubmarkets = [];
    
    for (const marketGroup of marketGroups) {
      if (pod?.markets.includes(marketGroup.key)) {
        allSubmarkets.push(...marketGroup.markets);
      }
    };

    return allSubmarkets;
  }, [pod, marketGroups]);

  useEffect(() => {
    const selectedProvider = allProviders.find(provider => provider.id === selectedProviderId);

    if (!selectedProvider) {
      setError(undefined);
      return;
    }

    const podPhysicianFilled = pod?.providers.some(provider => provider.id === pod.leadProviderId && provider.status === 'ACTIVATED') ?? false;
    const podCliniciansFilled = (pod?.providers.filter(provider => getProviderCategoryByPosition(provider) === 'Clinician' && provider.status === 'ACTIVATED').length ?? 0) >= MAX_POD_CLINICIANS;
    const category = getProviderCategory(selectedProvider);

    if (category === 'Clinician' && podCliniciansFilled) {
      setError(`This pod has no open slots for clinicians (${MAX_POD_CLINICIANS} of ${MAX_POD_CLINICIANS} occupied)`, true);
      return;
    }

    if (category === 'Physician' && podPhysicianFilled) {
      setError(`A physician has already been assigned to this pod.`, true);
      return;
    }

    const providerOverlapsPodMarkets = allSubmarketsInPod.some(podSubmarket => selectedProvider.markets.includes(podSubmarket));

    if (!providerOverlapsPodMarkets) {
      setError(`This provider isn’t active in any of the pod’s market groups.`, true);
      return;
    }

    setError(undefined);
  }, [selectedProviderId, pod, allProviders, allSubmarketsInPod]);

  // If the error passed in by prop changes, propagate it to the component's error state.
  useEffect(() => {
    setError(parentError);
  }, [parentError]);

  return (
    <Dialog
      hidden={!show}
      dialogContentProps={{
        type: DialogType.largeHeader,
        title: 'Add provider',
        closeButtonAriaLabel: 'Close',
      }}
    >
      <div className={styles.fieldStack}>
        <SearchBox
          value={searchTerm}
          onChange={(_, value) => setSearchTerm(value ?? '')}
          placeholder='Search for providers...'
        />
        <div className={styles.results}>
          <PodProviderSelector
            options={filteredProviders}
            selectedKey={selectedProviderId}
            onChange={(key) => setSelectedProviderId(key)}
          />
        </div>
        <PodErrorDrawer show={!!errorText} text={errorText ?? ''} className={styles.error}/>
      </div>
      <div className={styles.footerWrapper}>
        <DialogFooter styles={{ actions: { lineHeight: '1em', width: 'auto' } }}>
          <div className={styles.statusContainer}>
            { loading && (
              <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                <PodStatusSpinner state={PodStatusSpinnerState.loading} />
              </div>
            )}
            <PrimaryButton
              disabled={errorDisable || selectedProviderId === null || loading}
              onClick={submitCallback}
            >
              {loading ? 'Adding...' : 'Add'}
            </PrimaryButton>
            <DefaultButton onClick={dismissCallback}>Cancel</DefaultButton>
          </div>
        </DialogFooter>
      </div>
    </Dialog>
  )
};

export default AddProviderDialog;